import * as THREE from "three";
import Callout from "./Callout";
import { assert } from "./utilities/ErrorHandling";
import SpriteBase from "./SpriteBase";
import HighlightSphere from "./HighlightSphere";
import { LightTypes, RenderLayers } from '../globals';

let _geometry;
const tempPosition = new THREE.Vector3();
const tempRotation = new THREE.Quaternion();
const tempScale = new THREE.Vector3();
const identityQuat = new THREE.Quaternion();

class ScopeLight extends SpriteBase
{
	static spriteScale = new THREE.Vector3(0.1, 0.1, 0.1);

	color: THREE.Color;
	material;
	helper: THREE.Object3D;
	
	constructor(type = LightTypes.Directional, sourceObject?: ScopeLight)
	{
		super();

		if (sourceObject)
		{
			this.userData = JSON.parse(JSON.stringify(sourceObject.userData));
		}
		this.userData.lightType = type;
		this.color = new THREE.Color(0xffffff);

		// construct a plane for the sprite material if necessary
		if (!_geometry)
		{
			_geometry = new THREE.BufferGeometry();

			const float32Array = new Float32Array([
				- 0.5, - 0.5, 0, 0, 0,
				0.5, - 0.5, 0, 1, 0,
				0.5, 0.5, 0, 1, 1,
				- 0.5, 0.5, 0, 0, 1
			]);

			const interleavedBuffer = new THREE.InterleavedBuffer(float32Array, 5);

			_geometry.setIndex([0, 1, 2, 0, 2, 3]);
			_geometry.setAttribute('position', new THREE.InterleavedBufferAttribute(interleavedBuffer, 3, 0, false));
			_geometry.setAttribute('uv', new THREE.InterleavedBufferAttribute(interleavedBuffer, 2, 3, false));
		}

		this.geometry = _geometry;
		this.material = ScopeLight.loadSpriteMaterial(`${process.env.PUBLIC_URL}/bulb.png`);
		this.center = new THREE.Vector2(0.5, 0.5);
		this.helper = new THREE.Object3D();
		switch (this.userData.lightType)
		{
			case LightTypes.Directional:
				{
					this.name = "Directional light";
					this.userData.light = new THREE.DirectionalLight(this.color, 1);
					this.rotation.set(THREE.MathUtils.degToRad(-130), THREE.MathUtils.degToRad(30), 0);

					const target = new THREE.Object3D();

					this.attach(target);
					target.position.set(0, 0, -1);
					this.userData.light.target = target;					

					const size = 1.5;
					let geometry = new THREE.BufferGeometry();
					geometry.setAttribute('position', new THREE.Float32BufferAttribute([
						- size, size, 0,
						size, size, 0,
						size, - size, 0,
						- size, - size, 0,
						- size, size, 0
					], 3));

					const material = new THREE.LineBasicMaterial({ fog: false });
					const lightPlane = new THREE.Line(geometry, material);
					this.helper.add(lightPlane);

					geometry = new THREE.BufferGeometry();
					geometry.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 0, 0, -3], 3));

					const targetLine = new THREE.Line(geometry, material);
					this.helper.add(targetLine);

					this.userData.light.userData.hideInHierarchy = true;
					target.userData.hideInHierarchy = true;
					target.userData.ignoreExport = true;
				}
				break;
			case LightTypes.Point:
				{
					this.name = "Point light";
					this.userData.light = new THREE.PointLight(this.color, 1, 10);
					const geometry1 = new THREE.CircleGeometry(1, 32);
					const geometry2 = geometry1.clone();
					const geometry3 = geometry1.clone();
					geometry2.rotateX(THREE.MathUtils.degToRad(90));
					geometry3.rotateY(THREE.MathUtils.degToRad(90));

					const material = new THREE.LineBasicMaterial({ fog: false });
					const circle1 = new THREE.LineLoop(geometry1, material);
					const circle2 = new THREE.LineLoop(geometry2, material);
					const circle3 = new THREE.LineLoop(geometry3, material);

					this.helper.add(circle1);
					this.helper.add(circle2);
					this.helper.add(circle3);

					this.userData.light.userData.hideInHierarchy = true;
				}
				break;
			case LightTypes.Spot:
				{
					this.name = "Spot light";
					this.userData.light = new THREE.SpotLight(this.color, 1);
					this.userData.light.angle = THREE.MathUtils.degToRad(30);
					this.userData.light.distance = 10;
					this.rotation.set(THREE.MathUtils.degToRad(-90), 0, 0);

					const target = new THREE.Object3D();
					this.attach(target);
					target.position.set(0, 0, -1);
					this.userData.light.target = target;

					const geometry = new THREE.BufferGeometry();

					const positions = [
						0, 0, 0, 0, 0, 1,
						0, 0, 0, 1, 0, 1,
						0, 0, 0, - 1, 0, 1,
						0, 0, 0, 0, 1, 1,
						0, 0, 0, 0, - 1, 1
					];

					for (let i = 0, j = 1, l = 32; i < l; i++, j++)
					{
						const p1 = (i / l) * Math.PI * 2;
						const p2 = (j / l) * Math.PI * 2;

						positions.push(
							Math.cos(p1), Math.sin(p1), 1,
							Math.cos(p2), Math.sin(p2), 1
						);
					}

					geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
					geometry.scale(-1, -1, -1);
					const material = new THREE.LineBasicMaterial({ fog: false });
					this.helper = new THREE.LineSegments(geometry, material);

					this.userData.light.userData.hideInHierarchy = true;
					target.userData.hideInHierarchy = true;
					target.userData.ignoreExport = true;
				}
				break;
			default:
				console.error("Unknown light type: " + this.userData.lightType);
				break;
		}

		this.attach(this.userData.light);
		this.add(this.helper);
		this.userData.light.position.set(0, 0, 0);
		this.helper.visible = false;
		this.userData.lightColor = '0xffffff';
		this.userData.lightIntensity = 1.0;
		this.layers.set(RenderLayers.MainCameraOnly);
		this.helper.traverse(function(child)
		{
			child.layers.set(RenderLayers.MainCameraOnly);
			child.userData.hideInHierarchy = true;
			child.userData.ignoreExport = true;
		});
	}

	clone(recursive?: boolean)
	{
		return new ScopeLight(this.userData.lightType, this).copy(this) as this;
	}

	copy(source, recursive = true)
	{
		this.name = source.name;
		this.up.copy(source.up);
		this.position.copy(source.position);
		this.quaternion.copy(source.quaternion);
		this.scale.copy(source.scale);

		this.matrix.copy(source.matrix);
		this.matrixWorld.copy(source.matrixWorld);

		this.matrixAutoUpdate = source.matrixAutoUpdate;
		this.matrixWorldNeedsUpdate = source.matrixWorldNeedsUpdate;

		this.layers.mask = source.layers.mask;
		this.visible = source.visible;

		this.castShadow = source.castShadow;
		this.receiveShadow = source.receiveShadow;

		this.frustumCulled = source.frustumCulled;
		this.renderOrder = source.renderOrder;

		this.userData.light.intensity = source.userData.lightIntensity;
		this.userData.lightIntensity = source.userData.lightIntensity;
		this.userData.light.angle = source.userData.light.angle;
		this.userData.lightColor = source.userData.lightColor;
		this.userData.light.color.copy(source.userData.light.color);
		this.userData.light.distance = source.userData.light.distance;

		if (recursive === true)
		{
			for (let i = 0; i < source.children.length; i++)
			{
				const child = source.children[i];
				if (!child.userData.hideInHierarchy || child instanceof Callout || child instanceof HighlightSphere)
				{
					this.add(child.clone());
				}
			}

		}

		return this;
	}

	setLightIntensity(value)
	{
		this.userData.lightIntensity = value;
		this.userData.light.intensity = value;
	}

	getLightIntensity()
	{
		return this.userData.lightIntensity;
	}

	setLightColor(value)
	{
		if (value.length >= 6)
		{
			value = value.substr(value.length - 6);
		}
		value = '0x' + value;

		this.userData.lightColor = value;
		this.userData.light.color.setHex(value);
	}

	setLightRange(value)
	{
		this.userData.light.distance = value;
	}

	getLightAngle()
	{
		return this.userData.light.angle;
	}

	setLightAngle(value)
	{
		this.userData.light.angle = value;
	}

	getLightColor()
	{
		return this.userData.lightColor.substring(2);
	}

	getLightRange()
	{
		return this.userData.light.distance;
	}

	onSelected()
	{
		this.helper.visible = true;
	}

	onDeselected()
	{
		this.helper.visible = false;
	}

	updateMatrixWorld(force)
	{
		if (this.matrixAutoUpdate) this.updateMatrix();

		if (this.matrixWorldNeedsUpdate || force)
		{

			if (this.parent === null)
			{

				this.matrixWorld.copy(this.matrix);

			}
			else
			{

				this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix);

			}

			this.matrixWorldNeedsUpdate = false;

			force = true;
		}


		// update children
		const children = this.children;

		for (let i = 0, l = children.length; i < l; i++)
		{
			children[i].updateMatrixWorld(force);
		}

		//force scale to 1 world unit before rendering. we never want the the light to be scaled
		this.matrixWorld.decompose(tempPosition, tempRotation, tempScale);
		this.matrixWorld.compose(tempPosition, tempRotation, ScopeLight.spriteScale);
		tempScale.set(1, 1, 1);
		this.userData.light.matrixWorld.compose(tempPosition, tempRotation, tempScale);

		switch (this.userData.lightType)
		{
			case LightTypes.Directional:
				for (const child of this.helper.children)
				{
					child.matrixWorld.decompose(tempPosition, tempRotation, tempScale);
					tempScale.set(1, 1, 1);
					child.matrixWorld.compose(tempPosition, tempRotation, tempScale);
					(child as any).material?.color.copy(this.userData.light.color);
				}
				break;
			case LightTypes.Spot:
				const coneLength = this.userData.light.distance ? this.userData.light.distance : 1000;
				const coneWidth = coneLength * Math.tan(this.userData.light.angle);
				this.helper.matrixWorld.compose(tempPosition, tempRotation, new THREE.Vector3(coneWidth, coneWidth, coneLength));
				(this.helper as any).material?.color.copy(this.userData.light.color);
				break;
			case LightTypes.Point:
				for (const child of this.helper.children)
				{
					(child as any).material?.color.copy(this.userData.light.color);
					child.matrixWorld.decompose(tempPosition, tempRotation, tempScale);
					tempScale.set(this.userData.light.distance, this.userData.light.distance, this.userData.light.distance);
					child.matrixWorld.compose(tempPosition, identityQuat, tempScale);
				}
				break;
			default:
				assert(false, "unrecognized light type: " + this.userData.lightType);
		}
	}
}

export default ScopeLight;
